import React, {
  useMemo,
  useState,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import {
  DataGridPremium,
  GridActionsCellItem,
  GridRowParams,
  useGridApiRef,
  useGridApiContext,
  useGridRootProps,
  gridRowNodeSelector,
} from '@mui/x-data-grid-premium';
import type {
  GridFilterModel,
  GridSortModel,
  GridColDef,
  GridGroupNode,
  GridRowHeightParams,
  GridRenderCellParams,
  GridColumnVisibilityModel,
  GridCellParams,
} from '@mui/x-data-grid-premium';
import {
  Info,
  Edit,
  Trash2,
  Building,
  ChevronDown,
  Download,
  Bug,
  MoreVertical,
  Upload,
  AlignJustify,
  List,
  RotateCcw,
  ShieldCheck,
} from 'lucide-react';
import { useTableDensity } from '../shared/hooks/useTableDensity';
import { useSessionStorageState } from '../shared/hooks/useSessionStorageState';
import Button from '@mui/material/Button';
import LgmButton from './LgmButton';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import CircularProgress from '@mui/material/CircularProgress';
import type { FlatProduct } from '../services/api/domains/products/types';
import { useProductsFlatPage } from '../services/api/domains/products/hooks';
import { useApi } from '../services/api/provider';
import { ProductService } from '../services/api/domains/products/service';
import { fetchAllFlatProducts } from '../services/api/domains/products/fetchAllFlatProducts';
import { buildExcelWorkbook } from './buildExcelWorkbook';
import type { ExcelColumnDef } from './buildExcelWorkbook';
import { getProductDisplayName } from '../utils/productNameHelpers';
import DisplayNameSourceIndicator from './DisplayNameSourceIndicator';
import type { DisplayNameSource } from '../services/api/domains/products/types';
import { DISPLAY_NAME_SOURCE } from '../services/api/domains/products/types';
import type {
  AdvancedSearchQuery,
  SearchRule,
  FieldOperator,
} from './TableSearch/types';
import { createEmptyAdvancedQuery } from './TableSearch/types';
import { UnifiedSearchBar, UnifiedSearchState } from './UnifiedSearchBar';
import { toFlatAdvancedSearchConfig } from '../domains/products/adapters/searchConfigAdapter';
import {
  toDataGridColumns,
  getColumnVisibilityModel,
  getDedupFieldKeys,
} from './ProductsRowGroupingDataGrid.adapter';
import {
  getGroupableFields,
  FLAT_PRODUCT_FIELDS,
} from './ProductsRowGroupingDataGrid.fields';
import { GroupingControls } from './GroupingControls';
import { dataGridStyles } from './shared/DataGridShared';
import { deduplicateRows } from '../utils/deduplicateRows';
import {
  computeCollapsingKeys,
  computeShouldAutoExpand,
  computeSingleProductGroupRows,
  findAncestorNodeByField,
} from '../utils/collapsingUtils';
import {
  STORAGE_KEY_GROUPING,
  STORAGE_KEY_GROUPING_PRESET,
  STORAGE_KEY_GROUPING_PRESETS,
  STORAGE_KEY_CUSTOM_PRESETS,
  STORAGE_KEY_FILTER_PRESETS,
  STORAGE_KEY_FILTER_ACTIVE,
  STORAGE_KEY_COLUMN_VISIBILITY,
  STORAGE_KEY_COLUMN_VISIBILITY_DEFAULT_VERSION,
  STORAGE_KEY_COLUMN_ORDER,
  STORAGE_KEY_GRID_FILTER_MODEL,
} from '../utils/storageKeys';
import { BUILT_IN_FILTER_PRESETS } from '../domains/products/filterPresets';
import { GRAY_300, GRAY_400, GRAY_500, GRAY_700 } from 'theme/grayScale';
import { COLOR_DANGER, COLOR_SUCCESS } from 'theme/colors';
import useAuth from '../auth/auth';
import { useABTest } from '../contexts/ABTestContext';
import ValidateProductsModal from './modals/ValidateProductsModal';

/**
 * ProductsRowGroupingDataGrid - POC Component for Dynamic Row Grouping
 *
 * This component demonstrates MUI DataGrid Premium's native row grouping capabilities
 * with the new "multiple" column mode, where each grouping level displays in its own column.
 *
 * Key Features:
 * - Uses rowGroupingColumnMode="multiple" for proper column alignment
 * - User-configurable grouping levels with add/remove controls
 * - Uses realistic fixture data from stage environment
 *
 * Phase 1 Goal: Validate that MUI's native row grouping solves the alignment issue
 * Phase 2 Goal: Add user controls to dynamically configure grouping levels
 */

// Available dimensions for grouping (derived from field registry)
const GROUPABLE_DIMENSIONS = getGroupableFields().map(field => ({
  field: field.key as string,
  label: field.label,
}));

// Default-visible fields (2026-08-27 redesign): only these show out of the
// box. Includes the two Sourcing grouping columns (preferred_product_name,
// manufacturer_name) so they stay visible as ordinary flat columns if the
// user later removes them from grouping — while active as grouping
// dimensions, their flat-column form is separately forced hidden by
// getColumnVisibilityModel(groupingModel), which is merged on top of this
// set (see columnVisibilityModel below). The always-shown Actions column is
// not part of this model at all (see the Actions colDef). Every other
// FLAT_PRODUCT_FIELDS key defaults to hidden; derived rather than
// hand-maintained so a newly-added field is hidden by default without
// needing this list updated.
const VISIBLE_BY_DEFAULT = new Set<string>([
  'preferred_product_name',
  'manufacturer_name',
  'is_cas_verified',
  'is_ndc_linked',
  'comments',
]);

// Fallback used when no localStorage value exists (first visit / cleared
// storage) AND the one-time forced-default migration below (see
// STORAGE_KEY_COLUMN_VISIBILITY_DEFAULT_VERSION) for existing users.
const DEFAULT_COLUMN_VISIBILITY: Record<string, boolean> = Object.fromEntries(
  FLAT_PRODUCT_FIELDS.filter(f => !VISIBLE_BY_DEFAULT.has(f.key as string)).map(
    f => [f.key as string, false]
  )
);

// Bump this string to force every user's saved column visibility back to
// DEFAULT_COLUMN_VISIBILITY exactly once (see the columnVisibilityState
// initializer below) — e.g. after a deliberate default-columns redesign.
// Users' subsequent manual changes are respected again immediately after.
const COLUMN_VISIBILITY_DEFAULT_VERSION = '2026-08-27-cas-ndc-comments';

// -------------------------------------------------------------------------
// Filter preset helpers
// -------------------------------------------------------------------------

// Convert built-in filter presets (products domain format) to UnifiedSearchBar format
const PRODUCTS_UNIFIED_PRESETS = BUILT_IN_FILTER_PRESETS.map(p => ({
  id: p.name,
  label: p.name,
  query: p.query,
}));

// Default filter — "All Products" (no filters applied), not a blank/"Custom" state.
const PRODUCTS_DEFAULT_PRESET = PRODUCTS_UNIFIED_PRESETS.find(
  p => p.id === 'All Products'
);

// Field configs for UnifiedSearchBar (includes async-select for relational fields)
const PRODUCTS_FIELD_CONFIGS = toFlatAdvancedSearchConfig();

/**
 * Merges two AdvancedSearchQuery objects by AND-combining their filter groups.
 * Returns undefined when both are empty or absent.
 */
function mergeFilterQueries(
  base: AdvancedSearchQuery | undefined,
  additional: AdvancedSearchQuery | undefined
): AdvancedSearchQuery | undefined {
  const cleanBase = base?.filterGroups?.length ? base : undefined;
  const cleanAdditional = additional?.filterGroups?.length
    ? additional
    : undefined;
  if (!cleanBase && !cleanAdditional) return undefined;
  if (!cleanBase) return cleanAdditional;
  if (!cleanAdditional) return cleanBase;
  return {
    filterGroups: [...cleanBase.filterGroups, ...cleanAdditional.filterGroups],
    groupOperator: 'AND',
  };
}

// Custom preset type
interface CustomPreset {
  name: string;
  model: string[];
}

/**
 * Format a grouping key for display, applying boolean icon rendering when the
 * field's dataType is 'boolean'. Used by both GroupRowCell and the stranded
 * breadcrumb path so rendering is consistent everywhere.
 */
function formatGroupingKey(field: string, val: unknown): React.ReactNode {
  const fieldDef = FLAT_PRODUCT_FIELDS.find(f => f.key === field);
  if (fieldDef?.dataType === 'boolean') {
    if (val === null || val === undefined) return '—';
    const isTrue = val === true || val === 1 || val === 'true';
    const label = fieldDef.label;
    return isTrue ? (
      <>
        <span style={{ color: COLOR_SUCCESS, fontWeight: 'bold' }}>✓</span>{' '}
        {label}
      </>
    ) : (
      <>
        <span style={{ color: COLOR_DANGER, fontWeight: 'bold' }}>✗</span> Not{' '}
        {label}
      </>
    );
  }
  return String(val ?? '(No value)');
}

// Default grouping patterns. SOURCING is the out-of-the-box default; NONE
// (flat list, no collapsed rows) is listed first so it leads the grouping
// dropdown menu.
const DEFAULT_PATTERNS = {
  NONE: {
    shortLabel: 'None',
    label: 'None — flat list, no collapsed rows',
    model: [] as string[],
  },
  SOURCING: {
    shortLabel: 'Sourcing',
    label: 'Sourcing: Product Name → Manufacturer Company Name',
    model: ['preferred_product_name', 'manufacturer_name'],
  },
} as const;

type PresetKey = keyof typeof DEFAULT_PATTERNS | 'CUSTOM' | `custom_${string}`;

// Default column order: a stable merge, not a "search order first, then
// leftovers" concatenation. A plain concatenation (an earlier version of
// this derivation) always pushes every field lacking its own search-surfaced
// key to the very end, regardless of its registry position -- e.g.
// manufacturer_name/grade_name (grid/export only, not search-surfaced,
// since Advanced Search represents them via the separate manufacturer_id/
// grade_id relation fields instead) would land after "Modified Date",
// stranded away from their Manufacturer-fields/Grade-fields group siblings,
// even though every *other* field in those groups is search-surfaced and
// stays in place. The merge below keeps every non-search-surfaced field
// pinned to its exact field-registry slot (i.e. its natural group), and
// only reorders the search-surfaced fields AMONG THEMSELVES to match
// Advanced Search's order -- same slot count, same non-search neighbors,
// just a different search-surfaced key filling each search-surfaced slot.
// Deliberately does NOT map Advanced Search's four relation-ID fields
// (manufacturer_id/facility_id/grade_id/therapeutic_area_id) back to their
// grid-column counterparts -- those IDs aren't grid column keys at all, so
// they're simply absent from the merge, and their grid-column counterparts
// are treated as ordinary non-search-surfaced fields (pinned in place) when
// not independently search-surfaced under their own scalar key.
const DEFAULT_COLUMN_ORDER: string[] = (() => {
  const gridKeys = new Set(FLAT_PRODUCT_FIELDS.map(f => f.key as string));
  const registryOrder = FLAT_PRODUCT_FIELDS.map(f => f.key as string);
  const searchSurfacedSet = new Set(
    PRODUCTS_FIELD_CONFIGS.map(c => c.key).filter(key => gridKeys.has(key))
  );
  const searchOrderQueue = PRODUCTS_FIELD_CONFIGS.map(c => c.key).filter(key =>
    searchSurfacedSet.has(key)
  );
  let i = 0;
  const merged = registryOrder.map(key =>
    searchSurfacedSet.has(key) ? searchOrderQueue[i++] : key
  );

  // Default-visible non-grouping columns (2026-08-27 redesign) lead the
  // order, in this exact sequence: CAS Verified, NDC Linked, Comments. The
  // two Sourcing grouping columns (Product Name, Manufacturer Company Name)
  // aren't part of this array at all — MUI's row-grouping auto-places
  // grouping columns before every regular column, so they lead regardless.
  const LEADING_DEFAULT_VISIBLE = [
    'is_cas_verified',
    'is_ndc_linked',
    'comments',
  ];
  const leadingSet = new Set(LEADING_DEFAULT_VISIBLE);
  return [
    ...LEADING_DEFAULT_VISIBLE,
    ...merged.filter(key => !leadingSet.has(key)),
  ];
})();

/**
 * Custom cell renderer for visible group rows.
 * Uses public MUI hooks (useGridApiContext, useGridRootProps) to replicate
 * the essential expand/collapse behavior of GridGroupingCriteriaCell.
 * Must be defined outside the main component so hooks are called consistently.
 */
const GroupRowCell: React.FC<{
  params: GridRenderCellParams;
  displayNameSource?: DisplayNameSource | null;
  sourceLabel?: string;
}> = ({ params, displayNameSource, sourceLabel }) => {
  const apiContextRef = useGridApiContext();
  const rootProps = useGridRootProps();
  const groupNode = params.rowNode as GridGroupNode;
  const isExpanded = groupNode.childrenExpanded ?? false;
  const Icon = isExpanded
    ? rootProps.slots.groupingCriteriaCollapseIcon
    : rootProps.slots.groupingCriteriaExpandIcon;

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      apiContextRef.current.setRowChildrenExpansion(groupNode.id, !isExpanded);
      apiContextRef.current.setCellFocus(groupNode.id, params.field);
      e.stopPropagation();
    },
    [apiContextRef, groupNode.id, isExpanded, params.field]
  );

  // Boolean columns get colored icon + label text; all others fall back to string
  const groupDisplayValue = useMemo(() => {
    const field = groupNode.groupingField;
    if (!field) return String(groupNode.groupingKey ?? '(No value)');
    return formatGroupingKey(field, groupNode.groupingKey);
  }, [groupNode.groupingField, groupNode.groupingKey]);

  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <div
        role="button"
        tabIndex={-1}
        aria-label={isExpanded ? 'Collapse row' : 'Expand row'}
        onClick={handleClick}
        onKeyDown={e => {
          if (e.key === ' ') e.stopPropagation();
        }}
        style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
        <Icon />
      </div>
      <DisplayNameSourceIndicator
        displayNameSource={displayNameSource ?? null}
        sourceLabel={sourceLabel}>
        <span style={{ marginLeft: '4px' }}>{groupDisplayValue}</span>
      </DisplayNameSourceIndicator>
    </div>
  );
};

interface ProductsRowGroupingDataGridProps {
  /** Increment this counter after a successful product save to force a grid refresh. */
  refreshTrigger?: number;
  // Action handlers (permission-based visibility)
  onInfo?: (product: FlatProduct) => void;
  onEdit?: (product: FlatProduct) => void;
  onDelete?: (product: FlatProduct) => void;
  /** Opens the Upload Product Sheet dialog — pass only when the user has permission. */
  onUpload?: () => void;
  /**
   * Deep-link filter from another view (manufacturer-product-linking, Task
   * 2.2) — when present, seeds advanced search to this manufacturer_id on
   * mount, REPLACING (not merging with) any existing search state, since the
   * deep link's whole purpose is showing only that manufacturer's products.
   */
  initialManufacturerFilter?: {
    manufacturerId: number;
    manufacturerName?: string;
  } | null;
  /** Called once the initial filter above has been applied, so the parent can clear its pending state. */
  onInitialFilterConsumed?: () => void;
  /** Opens the given product's manufacturer, filtered (manufacturer-product-linking, Task 4.1). */
  onViewManufacturer?: (
    manufacturerId: number,
    manufacturerName?: string
  ) => void;
}

export const ProductsRowGroupingDataGrid: React.FC<
  ProductsRowGroupingDataGridProps
> = ({
  refreshTrigger,
  onInfo,
  onEdit,
  onDelete,
  onUpload,
  initialManufacturerFilter,
  onInitialFilterConsumed,
  onViewManufacturer,
}) => {
  // State for grouping model. SESSION-scoped: resets to SOURCING (the
  // product default) at the start of each new tab/window, survives refresh
  // + in-app navigation.
  const [groupingModel, setGroupingModel] = useState<string[]>(() => {
    try {
      // If the stored active preset is a known built-in key, always use the
      // current preset definition — this ensures definition changes (e.g. field
      // order updates) are automatically applied on the next page load and stale
      // sessionStorage models don't override the canonical definition.
      const savedPreset = sessionStorage.getItem(STORAGE_KEY_GROUPING_PRESET);
      if (
        savedPreset &&
        Object.prototype.hasOwnProperty.call(DEFAULT_PATTERNS, savedPreset)
      ) {
        return [
          ...DEFAULT_PATTERNS[savedPreset as keyof typeof DEFAULT_PATTERNS]
            .model,
        ];
      }
      const saved = sessionStorage.getItem(STORAGE_KEY_GROUPING);
      if (saved !== null) {
        const parsed = JSON.parse(saved);
        // Array.isArray only — an intentionally empty array (None grouping)
        // is a valid saved state and must not fall through to the default.
        if (Array.isArray(parsed)) {
          return parsed;
        }
      }
    } catch (err) {
      console.warn('Failed to load grouping model from sessionStorage:', err);
    }
    return [...DEFAULT_PATTERNS.SOURCING.model];
  });

  // State for active preset. SESSION-scoped (see groupingModel above).
  const [activePreset, setActivePreset] = useState<PresetKey>(() => {
    try {
      const saved = sessionStorage.getItem(STORAGE_KEY_GROUPING_PRESET);
      if (
        saved &&
        (saved === 'CUSTOM' ||
          Object.prototype.hasOwnProperty.call(DEFAULT_PATTERNS, saved) ||
          saved.startsWith('custom_'))
      ) {
        return saved as PresetKey;
      }
    } catch (err) {
      console.warn('Failed to load active preset from sessionStorage:', err);
    }
    return 'SOURCING';
  });

  // State for custom grouping presets (canonical key: lgm_grouping_presets).
  // 8.4.1 Migration: on first load, move any data from the old
  // STORAGE_KEY_CUSTOM_PRESETS key (lgm_products_custom_presets) to the
  // canonical STORAGE_KEY_GROUPING_PRESETS key, then delete the old key.
  const [customPresets, setCustomPresets] = useState<CustomPreset[]>(() => {
    try {
      const newKey = localStorage.getItem(STORAGE_KEY_GROUPING_PRESETS);
      if (!newKey) {
        const oldKey = localStorage.getItem(STORAGE_KEY_CUSTOM_PRESETS);
        if (oldKey) {
          localStorage.setItem(STORAGE_KEY_GROUPING_PRESETS, oldKey);
        }
      }
      // Always remove old key so it no longer exists
      localStorage.removeItem(STORAGE_KEY_CUSTOM_PRESETS);
    } catch {
      // ignore migration errors — non-critical
    }

    try {
      const saved = localStorage.getItem(STORAGE_KEY_GROUPING_PRESETS);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) {
          return parsed;
        }
      }
    } catch (err) {
      console.warn('Failed to load custom presets from localStorage:', err);
    }
    return [];
  });

  // ---- API service (for Excel export) ----
  const api = useApi();
  const productService = useMemo(() => new ProductService(api), [api]);

  // Validate Products devtool (issue #637, VPE-009) — admin RBAC (permanent)
  // + validate_products_devtool AB flag (temporary rollout gate).
  const { isAdmin } = useAuth();
  const { getTestValue } = useABTest();
  const isValidateProductsDevtoolEnabled = getTestValue(
    'validate_products_devtool'
  );
  const [isValidateProductsModalOpen, setIsValidateProductsModalOpen] =
    useState(false);

  // Export state machine
  // idle        → ready, shows download icon button
  // exporting   → in-flight, shows enabled Cancel button
  // cancelling  → abort sent, shows disabled “Cancelling…” + spinner until async resolves
  // error       → failed, shows download icon button + error banner
  type ExportState = 'idle' | 'exporting' | 'cancelling' | 'error';
  const [exportState, setExportState] = useState<ExportState>('idle');
  const [exportError, setExportError] = useState<string | null>(null);
  const exportAbortRef = useRef<AbortController | null>(null);

  // State for grouping toolbar dropdown
  const [groupingMenuAnchorEl, setGroupingMenuAnchorEl] =
    useState<HTMLElement | null>(null);

  // State for overflow (more actions) menu
  const [overflowMenuAnchorEl, setOverflowMenuAnchorEl] =
    useState<HTMLElement | null>(null);

  // Density toggle — shared with all pages via localStorage
  const { density, setDensity } = useTableDensity();

  // State for configure grouping modal
  const [isGroupingModalOpen, setIsGroupingModalOpen] = useState(false);
  const [pendingGroupingModel, setPendingGroupingModel] = useState<string[]>(
    []
  );

  // State for column visibility — load from localStorage or fall back to DEFAULT_COLUMN_VISIBILITY
  const [columnVisibilityState, setColumnVisibilityState] =
    useState<GridColumnVisibilityModel>(() => {
      try {
        // One-time forced-default migration: if this browser hasn't seen the
        // current COLUMN_VISIBILITY_DEFAULT_VERSION yet, override whatever
        // was saved with the new default exactly once (even if the user had
        // customized visibility under the old default), then record the
        // version so this never fires again — subsequent user changes are
        // respected immediately after, same as any other visibility edit.
        const savedVersion = localStorage.getItem(
          STORAGE_KEY_COLUMN_VISIBILITY_DEFAULT_VERSION
        );
        if (savedVersion !== COLUMN_VISIBILITY_DEFAULT_VERSION) {
          localStorage.setItem(
            STORAGE_KEY_COLUMN_VISIBILITY,
            JSON.stringify(DEFAULT_COLUMN_VISIBILITY)
          );
          localStorage.setItem(
            STORAGE_KEY_COLUMN_VISIBILITY_DEFAULT_VERSION,
            COLUMN_VISIBILITY_DEFAULT_VERSION
          );
          return DEFAULT_COLUMN_VISIBILITY;
        }

        const saved = localStorage.getItem(STORAGE_KEY_COLUMN_VISIBILITY);
        if (saved) {
          const parsed = JSON.parse(saved);
          if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
            return parsed as GridColumnVisibilityModel;
          }
        }
      } catch (err) {
        console.warn(
          'Failed to load column visibility from localStorage:',
          err
        );
      }
      return DEFAULT_COLUMN_VISIBILITY;
    });

  // Initial column order. SESSION-scoped: resets to the Sourcing-led default
  // at the start of each new tab/window; loaded once, passed via DataGrid initialState.
  const initialColumnOrder = useMemo<string[]>(() => {
    try {
      const saved = sessionStorage.getItem(STORAGE_KEY_COLUMN_ORDER);
      if (saved !== null) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) {
          return parsed as string[];
        }
      }
    } catch {
      // ignore — fall back to default column order
    }
    return DEFAULT_COLUMN_ORDER;
  }, []); // intentionally empty — computed once on mount

  // Column-header filter chips (GridFilterModel). SESSION-scoped: resets to
  // empty at the start of each new tab/window, survives refresh + navigation.
  const [filterModel, setFilterModel] = useSessionStorageState<GridFilterModel>(
    STORAGE_KEY_GRID_FILTER_MODEL,
    { items: [] }
  );

  // Search state — managed by UnifiedSearchBar, reflected here for API params.
  // Lazy-initialized from `initialManufacturerFilter` (Task 2.2) when present
  // -- REPLACES the default empty/basic state entirely (not merged), since a
  // deep link's purpose is showing only that manufacturer's products. Built
  // directly from this file's own SearchRule/AdvancedSearchQuery types rather
  // than the shared `buildSimpleFilterQuery` helper -- that helper returns a
  // structurally different AdvancedSearchQuery (from utils/fieldOperators.tsx,
  // where SearchRule.value is `string | string[]`) than the one used here
  // (TableSearch/types.ts, where value is `string | number | Date |
  // [Date,Date]`) -- two parallel type definitions for the same concept
  // elsewhere in this codebase, not something to paper over with a cast here.
  const [searchState, setSearchState] = useState<UnifiedSearchState>(() => {
    if (initialManufacturerFilter) {
      const rule: SearchRule = {
        id: 'initial-manufacturer-filter',
        field: 'manufacturer_id',
        operator: 'equals',
        value: initialManufacturerFilter.manufacturerId,
        label: initialManufacturerFilter.manufacturerName,
      };
      return {
        mode: 'advanced',
        basicTerm: '',
        advancedQuery: {
          filterGroups: [
            {
              id: 'initial-manufacturer-filter-group',
              logicalOperator: 'AND',
              rules: [rule],
            },
          ],
          groupOperator: 'AND',
        },
      };
    }
    return {
      mode: 'basic',
      basicTerm: '',
      advancedQuery: createEmptyAdvancedQuery(),
    };
  });

  // Tell the parent the initial filter has been consumed, so it can clear
  // its pending state. This component normally stays mounted across view
  // switches (keep-alive tabs) -- it only remounts (fresh lazy initializer
  // above) when LgmProductNavigator keys it on a fresh "View Products" click,
  // which is exactly the case where `initialManufacturerFilter` is set.
  // Listing the real dependencies rather than suppressing the lint rule:
  // `onInitialFilterConsumed` is a stable `useCallback` in the parent, and
  // calling it more than once is harmless (it just sets already-null state
  // to null again).
  useEffect(() => {
    if (initialManufacturerFilter) {
      onInitialFilterConsumed?.();
    }
  }, [initialManufacturerFilter, onInitialFilterConsumed]);

  // State for sorting and pagination (wired to DataGrid)
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: 'product_cas_name', sort: 'asc' }, // Default sort
  ]);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 50,
  });

  // Helper: Strip DataGrid grouping prefix from field names
  const stripGroupingPrefix = useCallback((fieldName: string): string => {
    // DataGrid adds prefixes like "__row_group_by_columns_group_manufacturer_name__"
    // We need to extract the actual field name
    const groupPrefix = '__row_group_by_columns_group_';
    const groupSuffix = '__';

    if (fieldName.startsWith(groupPrefix) && fieldName.endsWith(groupSuffix)) {
      // Extract the field name between prefix and suffix
      return fieldName.slice(groupPrefix.length, -groupSuffix.length);
    }

    return fieldName;
  }, []);

  const convertFilterModelToAdvancedSearch = useCallback(
    (model: GridFilterModel): AdvancedSearchQuery | undefined => {
      if (!model.items || model.items.length === 0) return undefined;

      // Map DataGrid operators to our FieldOperator types
      const mapOperator = (op: string): FieldOperator | null => {
        switch (op) {
          case 'contains':
            return 'contains';
          case 'equals':
          case 'is': // Boolean field operator or date "is" operator
            return 'equals';
          case 'startsWith':
          case 'endsWith':
            return 'contains'; // Fallback to contains
          case 'notEquals':
          case '!=':
          case 'isNot': // Boolean field negation
            return 'notEquals';
          case '>':
            return 'greaterThan';
          case '<':
            return 'lessThan';
          case 'after':
            return 'isAfter';
          case 'before':
            return 'isBefore';
          case 'onOrAfter':
          case '>=':
            return 'greaterThan'; // Date on or after maps to greaterThan
          case 'onOrBefore':
          case '<=':
            return 'lessThan'; // Date on or before maps to lessThan
          case 'isEmpty':
            return 'isEmpty';
          case 'isNotEmpty':
            return 'isNotEmpty';
          default:
            console.warn('[ProductsRowGroupingDataGrid] Unknown operator:', op);
            return null;
        }
      };

      // Convert filter items to search rules
      const noValueOperators = new Set(['isEmpty', 'isNotEmpty']);
      const rules: SearchRule[] = model.items
        .filter(
          item =>
            item.field &&
            item.operator &&
            (noValueOperators.has(item.operator) || item.value !== undefined)
        )
        .map((item, index) => {
          const mappedOperator = mapOperator(item.operator);
          if (!mappedOperator) {
            console.warn(
              '[ProductsRowGroupingDataGrid] Skipping item - no mapped operator'
            );
            return null;
          }

          // Strip grouping prefix from field name
          const actualField = stripGroupingPrefix(item.field);

          // Convert date values to ISO string format
          // isEmpty/isNotEmpty carry no value — pass empty string (backend ignores it)
          let value = noValueOperators.has(item.operator) ? '' : item.value;
          if (value instanceof Date) {
            value = value.toISOString();
          }

          const rule = {
            id: `rule-${index}`,
            field: actualField,
            operator: mappedOperator,
            value: value,
          };
          return rule;
        })
        .filter((rule): rule is SearchRule => rule !== null);

      if (rules.length === 0) return undefined;

      // Create a single filter group with all rules combined with AND
      return {
        filterGroups: [
          {
            id: 'group-1',
            logicalOperator: 'AND',
            rules,
          },
        ],
        groupOperator: 'AND',
      };
    },
    [stripGroupingPrefix]
  );

  // Memoize API params to prevent infinite re-renders
  const apiParams = useMemo(() => {
    // Extract sort parameters
    const userSortField =
      sortModel.length > 0
        ? stripGroupingPrefix(sortModel[0].field)
        : 'product_cas_name';
    // When the user has not overridden the sort (still on the hardcoded default
    // 'product_cas_name'), prefer the first grouping dimension as the API sort key.
    // This ensures top-level groups appear in alphabetical order regardless of
    // which preset is active (e.g. Sourcing uses preferred_product_name, not CAS name).
    const sortField =
      userSortField === 'product_cas_name' && groupingModel.length > 0
        ? groupingModel[0]
        : userSortField;
    const sortDirection =
      sortModel.length > 0 && sortModel[0].sort
        ? sortModel[0].sort.toUpperCase()
        : 'ASC';

    // Calculate pagination offset
    const offset = paginationModel.page * paginationModel.pageSize;

    // Convert column filter model to advancedSearch rules
    const columnFilterQuery = convertFilterModelToAdvancedSearch(filterModel);

    // Merge: advanced filter query (base) + column filter rules (AND on top)
    const advancedSearch = mergeFilterQueries(
      searchState.mode === 'advanced' ? searchState.advancedQuery : undefined,
      columnFilterQuery
    );

    const params = {
      search:
        searchState.mode === 'basic'
          ? searchState.basicTerm || undefined
          : undefined,
      advancedSearch,
      sortBy: sortField,
      sortDirection: sortDirection as 'ASC' | 'DESC',
      limit: paginationModel.pageSize,
      offset,
    };

    return params;
  }, [
    searchState,
    sortModel,
    paginationModel,
    filterModel,
    groupingModel,
    stripGroupingPrefix,
    convertFilterModelToAdvancedSearch,
  ]);

  // Fetch real data from API
  const {
    data: apiData,
    totalProducts: apiTotalProducts,
    isLoading: apiLoading,
    isError: apiError,
    refetch: apiRefetch,
  } = useProductsFlatPage(apiParams);

  // Reset to first page whenever search state changes
  useEffect(() => {
    setPaginationModel(prev => {
      if (prev.page === 0) {
        return prev;
      }
      return { ...prev, page: 0 };
    });
  }, [searchState]);

  // Re-fetch flat data whenever the parent increments refreshTrigger (e.g. after a save)
  useEffect(() => {
    if (refreshTrigger !== undefined && refreshTrigger > 0) {
      apiRefetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- apiRefetch is React Query's refetch, a stable callback reference for the lifetime of the observer
  }, [refreshTrigger]);

  // One-time cleanup: these keys used to hold permanent localStorage state
  // for grouping/column-order. They now live in sessionStorage — drop the
  // stale localStorage entries so they don't linger indefinitely.
  useEffect(() => {
    try {
      localStorage.removeItem(STORAGE_KEY_GROUPING);
      localStorage.removeItem(STORAGE_KEY_GROUPING_PRESET);
      localStorage.removeItem(STORAGE_KEY_COLUMN_ORDER);
    } catch {
      // ignore
    }
  }, []);

  // Persist grouping model to sessionStorage whenever it changes (session-scoped)
  useEffect(() => {
    try {
      sessionStorage.setItem(
        STORAGE_KEY_GROUPING,
        JSON.stringify(groupingModel)
      );
    } catch (err) {
      console.warn('Failed to save grouping model to sessionStorage:', err);
    }
  }, [groupingModel]);

  // Persist active preset to sessionStorage whenever it changes (session-scoped).
  // Stored as a raw string (not JSON-wrapped) to match the initializer's plain
  // sessionStorage.getItem() read above.
  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY_GROUPING_PRESET, activePreset);
    } catch (err) {
      console.warn('Failed to save active preset to sessionStorage:', err);
    }
  }, [activePreset]);

  // Persist custom presets to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem(
        STORAGE_KEY_GROUPING_PRESETS,
        JSON.stringify(customPresets)
      );
    } catch (err) {
      console.warn('Failed to save custom presets to localStorage:', err);
    }
  }, [customPresets]);

  // ---- Search handler ----

  const handleSearchChange = useCallback((state: UnifiedSearchState) => {
    setSearchState(state);
  }, []);

  // ---- Grouping preset handlers ----

  const handleApplyPreset = useCallback(
    (presetKey: keyof typeof DEFAULT_PATTERNS) => {
      const preset = DEFAULT_PATTERNS[presetKey];
      setGroupingModel([...preset.model]);
      setActivePreset(presetKey);
    },
    []
  );

  const handleGroupingModelChange = (model: string[]) => {
    setGroupingModel(model);
    setActivePreset('CUSTOM'); // Switch to custom when manually modified
  };

  const handleSaveAsPreset = (name: string) => {
    if (!name.trim()) return;

    const newPreset: CustomPreset = {
      name: name.trim(),
      model: [...pendingGroupingModel],
    };

    const existingIndex = customPresets.findIndex(
      p => p.name === newPreset.name
    );
    if (existingIndex >= 0) {
      const updated = [...customPresets];
      updated[existingIndex] = newPreset;
      setCustomPresets(updated);
    } else {
      setCustomPresets([...customPresets, newPreset]);
    }
    setActivePreset(`custom_${newPreset.name}`);
  };

  // Derive the active preset label for the grouping dropdown button
  const getGroupingLabel = (): string => {
    if (
      activePreset !== 'CUSTOM' &&
      Object.prototype.hasOwnProperty.call(DEFAULT_PATTERNS, activePreset)
    ) {
      return DEFAULT_PATTERNS[activePreset as keyof typeof DEFAULT_PATTERNS]
        .shortLabel;
    }
    if (activePreset.startsWith('custom_')) {
      const name = activePreset.substring(7);
      return customPresets.find(p => p.name === name)?.name ?? 'Custom';
    }
    return 'Custom';
  };

  // Detect which preset (if any) matches a given model
  const detectPresetForModel = (model: string[]): PresetKey => {
    const modelStr = JSON.stringify(model);
    for (const key of Object.keys(DEFAULT_PATTERNS) as Array<
      keyof typeof DEFAULT_PATTERNS
    >) {
      if (JSON.stringify([...DEFAULT_PATTERNS[key].model]) === modelStr)
        return key;
    }
    for (const preset of customPresets) {
      if (JSON.stringify(preset.model) === modelStr)
        return `custom_${preset.name}` as PresetKey;
    }
    return 'CUSTOM';
  };

  // Configure grouping modal handlers
  const handleOpenGroupingModal = () => {
    setPendingGroupingModel([...groupingModel]);
    setIsGroupingModalOpen(true);
  };

  const handleApplyGroupingModal = () => {
    setGroupingModel(pendingGroupingModel);
    setActivePreset(detectPresetForModel(pendingGroupingModel));
    setIsGroupingModalOpen(false);
  };

  const handleCloseGroupingModal = () => {
    setIsGroupingModalOpen(false);
  };

  const handleLoadCustomPreset = (preset: CustomPreset) => {
    setGroupingModel([...preset.model]);
    setActivePreset(`custom_${preset.name}`);
  };

  const handleDeleteCustomPreset = (presetName: string) => {
    setCustomPresets(customPresets.filter(p => p.name !== presetName));
    // If we're deleting the active preset, switch to CUSTOM
    if (activePreset === `custom_${presetName}`) {
      setActivePreset('CUSTOM');
    }
  };

  // Define columns from field registry
  const baseColumns = useMemo(() => toDataGridColumns() as GridColDef[], []);

  // Check if any action handlers are provided
  const hasActions = Boolean(
    onInfo || onEdit || onDelete || onViewManufacturer
  );

  // Calculate dynamic width for actions column
  const actionCount =
    (onInfo ? 1 : 0) +
    (onEdit ? 1 : 0) +
    (onDelete ? 1 : 0) +
    (onViewManufacturer ? 1 : 0);
  const actionsColumnWidth = Math.max(80, actionCount * 40 + 20);

  // Latest-callback refs: onInfo/onEdit/onDelete are typically passed as new
  // inline function references on every parent render. Reading them through a
  // ref (rather than closing over them directly) keeps `actionsColumn` — and
  // therefore the `columns` array below — referentially stable across
  // renders. This matters: MUI DataGrid resets column order to its natural
  // (unordered) state whenever the `columns` prop reference changes, which
  // would otherwise silently discard both the Sourcing-led default order and
  // any session-persisted user reordering on the very next parent re-render.
  const onInfoRef = useRef(onInfo);
  const onEditRef = useRef(onEdit);
  const onDeleteRef = useRef(onDelete);
  const onViewManufacturerRef = useRef(onViewManufacturer);
  useEffect(() => {
    onInfoRef.current = onInfo;
    onEditRef.current = onEdit;
    onDeleteRef.current = onDelete;
    onViewManufacturerRef.current = onViewManufacturer;
  });

  const apiRef = useGridApiRef();

  // Stable ref for the active grouping model, read inside actionsColumn's
  // memoized getActions/cellClassName (see computeOwnHeaderKey below) so
  // they never see a stale grouping config across renders.
  const groupingModelRef = useRef<string[]>([]);

  // Add actions column if handlers are provided
  const actionsColumn: GridColDef<FlatProduct> = useMemo(() => {
    return {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: actionsColumnWidth,
      pinnable: false,
      disableReorder: true,
      // Exactly one row across the WHOLE grid renders actions for a given
      // product (see winningActionRef) -- every other row tied to that
      // product (a different branch's header, a different branch's leaves)
      // renders nothing here, but MUI still draws each cell's normal
      // border. Suppress it on every row that isn't the last row of its OWN
      // branch (see isZoneLastRowRef) -- that includes the winning row
      // itself, whenever more of the same branch follows it -- so the whole
      // run reads as one seamless block. This is scoped per branch rather
      // than globally per product: a different branch of the SAME product
      // still needs its own boundary (e.g. two manufacturers), so only each
      // branch's own tail keeps a border. A group node is always safe to
      // blend forward (it's never the last row, always followed immediately
      // by a child); a node that could never hold any product's actions
      // (spans multiple products, e.g. a bare manufacturer or
      // facility-status bucket) keeps its normal border, which is what
      // supplies the visual boundary between different branches/products.
      cellClassName: (params: GridCellParams<FlatProduct>) => {
        const row = params.row as FlatProduct & { __treeDataPath__?: string };
        const isProductRow = row.product_id != null && !row.__treeDataPath__;
        if (isProductRow) {
          return isZoneLastRowRef.current.has(row.id)
            ? ''
            : 'lgm-actions-cell-noborder';
        }
        if (typeof params.id !== 'string') return '';
        const ownKey = params.id.startsWith('auto-generated-row-')
          ? params.id.slice('auto-generated-row-'.length)
          : params.id;
        return singleProductGroupRowsRef.current.has(ownKey)
          ? 'lgm-actions-cell-noborder'
          : '';
      },
      getActions: (params: GridRowParams<FlatProduct>) => {
        const actions = [];

        // Only show actions on product-level rows
        // In row grouping, leaf rows have the actual data
        // Group header rows are synthetic and won't have a valid product_id
        const row = params.row as FlatProduct & {
          __treeDataPath__?: string;
        };
        const isProductRow = row.product_id != null && !row.__treeDataPath__;

        let productRow: FlatProduct | null = null;

        if (isProductRow) {
          // Only the one globally-chosen winning leaf for this product_id
          // renders actions (see winningActionRef); every other leaf for
          // the same product, in any branch, stays blank.
          const winner = winningActionRef.current.get(row.product_id);
          if (winner?.type === 'leaf' && winner.id === row.id) {
            productRow = row;
          }
        } else if (typeof params.id === 'string') {
          const ownKey = params.id.startsWith('auto-generated-row-')
            ? params.id.slice('auto-generated-row-'.length)
            : params.id;
          // Only the one globally-chosen winning header key renders
          // actions; every other single-product group node (including
          // other valid headers for the SAME product in other branches)
          // stays blank.
          if (winningHeaderKeysRef.current.has(ownKey)) {
            productRow =
              (singleProductGroupRowsRef.current.get(ownKey) as
                FlatProduct | undefined) ?? null;
          }
        }

        if (!productRow) {
          return []; // Suppressed row, or a multi-product group header
        }
        const resolvedRow = productRow;

        if (onInfoRef.current) {
          actions.push(
            <GridActionsCellItem
              key="info"
              icon={<Info size={18} />}
              label="Info"
              title="Info"
              onClick={() => onInfoRef.current?.(resolvedRow)}
            />
          );
        }

        if (onEditRef.current) {
          actions.push(
            <GridActionsCellItem
              key="edit"
              icon={<Edit size={18} />}
              label="Edit"
              title="Edit"
              onClick={() => onEditRef.current?.(resolvedRow)}
            />
          );
        }

        if (onDeleteRef.current) {
          actions.push(
            <GridActionsCellItem
              key="delete"
              icon={<Trash2 size={18} />}
              label="Delete"
              title="Delete"
              onClick={() => onDeleteRef.current?.(resolvedRow)}
            />
          );
        }

        // Hidden when the product has no linked manufacturer (nullable field).
        // Explicit null check (not truthiness) -- manufacturer_id is an
        // auto-increment PK so 0 can't occur in practice, but a truthiness
        // check would incorrectly hide the action for it if it ever did.
        if (
          onViewManufacturerRef.current &&
          resolvedRow.manufacturer_id != null
        ) {
          const manufacturerId = resolvedRow.manufacturer_id;
          actions.push(
            <GridActionsCellItem
              key="view-manufacturer"
              icon={<Building size={18} />}
              label="View Manufacturer"
              title="View Manufacturer"
              onClick={() =>
                onViewManufacturerRef.current?.(
                  manufacturerId,
                  resolvedRow.manufacturer_name ?? undefined
                )
              }
            />
          );
        }

        return actions;
      },
    };
  }, [actionsColumnWidth]);

  // Combine base columns with actions column
  const columns = useMemo(
    () => (hasActions ? [...baseColumns, actionsColumn] : baseColumns),
    [baseColumns, actionsColumn, hasActions]
  );

  // Column visibility: merge user preference with grouping-based forced-hidden columns.
  // Grouping dimensions always win (a column in groupingModel is never shown as a flat column).
  const columnVisibilityModel = useMemo(
    () => ({
      ...columnVisibilityState,
      ...getColumnVisibilityModel(groupingModel), // grouping always overrides
    }),
    [columnVisibilityState, groupingModel]
  );

  // Excel export handler — fetches all pages, builds workbook, triggers download.
  // Column order and visibility are read from apiRef at call time so the export
  // matches exactly what the user sees (including any drag-reordering). Grouping
  // columns are included — they are hidden from the flat grid view (UI concern)
  // but still contain meaningful data in a flat export.
  const handleCancelExport = useCallback(() => {
    setExportState('cancelling');
    exportAbortRef.current?.abort();
  }, []);

  const handleExcelExport = useCallback(async () => {
    const controller = new AbortController();
    exportAbortRef.current = controller;
    setExportState('exporting');
    setExportError(null);
    try {
      // Build the ordered export column list from the live DataGrid column order.
      //
      // With rowGroupingColumnMode="multiple", MUI inserts synthetic columns at
      // the visual positions of grouped fields. Their field names follow the
      // pattern `__row_group_by_columns_group_{fieldName}__`. The corresponding
      // real data columns are still present in getAllColumns() but are moved/hidden.
      //
      // Strategy: iterate getAllColumns() in order; for each column resolve it to
      // a real FlatProduct field (stripping the synthetic prefix/suffix for
      // grouping columns). Skip columns that aren't data fields, are already
      // added (dedup), or are explicitly hidden by the user.
      const SYNTHETIC_PREFIX = '__row_group_by_columns_group_';
      const SYNTHETIC_SUFFIX = '__';

      const addedFields = new Set<string>();
      const liveColumns: ExcelColumnDef[] = [];

      for (const col of apiRef.current?.getAllColumns() ?? []) {
        // Resolve real field name: strip synthetic grouping prefix/suffix if present.
        let realField = col.field;
        if (
          realField.startsWith(SYNTHETIC_PREFIX) &&
          realField.endsWith(SYNTHETIC_SUFFIX)
        ) {
          realField = realField.slice(
            SYNTHETIC_PREFIX.length,
            -SYNTHETIC_SUFFIX.length
          );
        }

        // Skip if not a FlatProduct data field, already added, or user-hidden.
        const fieldDef = FLAT_PRODUCT_FIELDS.find(
          f => (f.key as string) === realField
        );
        if (!fieldDef) continue;
        if (addedFields.has(realField)) continue;
        if (columnVisibilityState[realField] === false) continue;

        addedFields.add(realField);
        liveColumns.push({ field: realField, headerName: fieldDef.label });
      }

      const filterParams = apiParams
        ? {
            search: apiParams.search,
            advancedSearch: apiParams.advancedSearch,
            sortBy: apiParams.sortBy,
            sortDirection: apiParams.sortDirection,
          }
        : {};

      const rows = await fetchAllFlatProducts(productService, filterParams, {
        signal: controller.signal,
      });
      const blob = await buildExcelWorkbook(rows, liveColumns);

      const today = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
      const filename = `lgm-products-export-${today}.xlsx`;
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = filename;
      anchor.click();
      URL.revokeObjectURL(url);
      setExportState('idle');
    } catch (err) {
      // Suppress abort errors — the user cancelled intentionally.
      if (err instanceof DOMException && err.name === 'AbortError') {
        setExportError(null);
        setExportState('idle');
      } else {
        // Surface a friendly message. Strip the raw URL from timeout messages.
        const raw = err instanceof Error ? err.message : 'Export failed';
        const friendly = raw.includes('timed out')
          ? `Export timed out — the server took too long to respond. Try again, or apply filters to reduce the data set before exporting.`
          : raw.includes('Unable to connect')
            ? `Could not reach the server. Check your connection and try again.`
            : raw;
        setExportError(friendly);
        setExportState('error');
      }
    } finally {
      exportAbortRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- apiRef is a stable React ref
  }, [productService, apiParams, columnVisibilityState]);

  // Persist column visibility whenever the user toggles a column.
  // Save the user state (without grouping-forced entries) so it stays correct if grouping changes.
  const handleColumnVisibilityModelChange = useCallback(
    (model: GridColumnVisibilityModel) => {
      const groupingForced = getColumnVisibilityModel(groupingModel);
      const userModel: GridColumnVisibilityModel = { ...model };
      // Strip grouping-forced keys so stored state is independent of current grouping
      Object.keys(groupingForced).forEach(k => delete userModel[k]);
      setColumnVisibilityState(userModel);
      try {
        localStorage.setItem(
          STORAGE_KEY_COLUMN_VISIBILITY,
          JSON.stringify(userModel)
        );
      } catch (err) {
        console.warn('Failed to save column visibility to localStorage:', err);
      }
    },
    [groupingModel]
  );

  // Persist column order whenever the user drags a column. Session-scoped:
  // resets to the Sourcing-led default at the start of each new tab/window.
  const handleColumnOrderChange = useCallback(() => {
    if (!apiRef.current) return;
    try {
      const ordered = apiRef.current
        .getAllColumns()
        .map((col: GridColDef) => col.field);
      sessionStorage.setItem(STORAGE_KEY_COLUMN_ORDER, JSON.stringify(ordered));
    } catch (err) {
      console.warn('Failed to save column order to sessionStorage:', err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- apiRef is a stable MutableRefObject; safe to omit
  }, []);

  // "Reset to Defaults" (overflow menu) — restores filter, grouping, and
  // column visibility/order to the out-of-the-box defaults: All Products,
  // Sourcing grouping, and the default-visible column set (CAS Verified,
  // NDC Linked, Comments, plus the two Sourcing grouping columns and the
  // always-shown Actions column). Also clears column-header filter chips.
  //
  // Writes the target values straight to the same storage keys every piece
  // of state above already hydrates from on mount, then reloads the page —
  // rather than pushing live state through each field's own change handler.
  // Those handlers are built for incremental, single-field user edits (e.g.
  // `handleColumnVisibilityModelChange` deliberately strips whatever the
  // *current* grouping forces, so it can be re-applied if grouping changes
  // later); reusing them for a simultaneous multi-field reset means each one
  // observes the others' updates mid-flight through stale closures. A reload
  // sidesteps that entirely: every `useState` initializer re-reads storage
  // fresh, in one consistent pass, with no ordering or timing to get right.
  const handleResetToDefaults = useCallback(() => {
    try {
      sessionStorage.removeItem(STORAGE_KEY_FILTER_ACTIVE);
      sessionStorage.setItem(
        STORAGE_KEY_GRID_FILTER_MODEL,
        JSON.stringify({ items: [] })
      );
      sessionStorage.setItem(STORAGE_KEY_GROUPING_PRESET, 'SOURCING');
      sessionStorage.setItem(
        STORAGE_KEY_GROUPING,
        JSON.stringify(DEFAULT_PATTERNS.SOURCING.model)
      );
      sessionStorage.setItem(
        STORAGE_KEY_COLUMN_ORDER,
        JSON.stringify(DEFAULT_COLUMN_ORDER)
      );
      localStorage.setItem(
        STORAGE_KEY_COLUMN_VISIBILITY,
        JSON.stringify(DEFAULT_COLUMN_VISIBILITY)
      );
    } catch (err) {
      console.warn('Failed to reset stored grid preferences:', err);
    }
    window.location.reload();
  }, []);

  // Prepare rows from API data
  const rows = useMemo((): FlatProduct[] => {
    return apiData || [];
  }, [apiData]);

  // Map from preferred_product_name value → display_name_source.
  // Used by groupingColDef to show the source indicator badge on group header rows
  // when preferred_product_name is a grouping dimension (e.g. SOURCING preset).
  const preferredNameSourceMap = useMemo(() => {
    const map = new Map<string, DisplayNameSource>();
    for (const row of rows) {
      const name = row.preferred_product_name;
      const source = row.display_name_source as DisplayNameSource | undefined;
      if (name && source && source !== DISPLAY_NAME_SOURCE.USER) {
        map.set(name, source);
      }
    }
    return map;
  }, [rows]);

  // Map from preferred_product_name value → manufacturer label (for 'manufacturer' source only).
  // Used to populate the chip/tooltip with the manufacturer(s) that provided the catalogue name.
  // Several manufacturers can share the exact same catalogue name for a product (e.g. two
  // suppliers both list "Azilsartan Medoxomil Potassium Salt") -- there's no single "winning"
  // manufacturer surfaced by the API (display_name_id isn't in the flat response), so rather than
  // arbitrarily picking one, show the first plus a "+N more" count of the other distinct
  // manufacturers associated with that name.
  const preferredNameManufacturerMap = useMemo(() => {
    const grouped = new Map<string, Map<number, string>>();
    for (const row of rows) {
      const name = row.preferred_product_name;
      const source = row.display_name_source as DisplayNameSource | undefined;
      const mfrId = row.manufacturer_id;
      const mfrName = row.manufacturer_name;
      if (
        name &&
        source === DISPLAY_NAME_SOURCE.MANUFACTURER &&
        mfrId != null &&
        mfrName
      ) {
        const mfrsById = grouped.get(name) ?? new Map<number, string>();
        mfrsById.set(mfrId, mfrName);
        grouped.set(name, mfrsById);
      }
    }
    const map = new Map<string, string>();
    grouped.forEach((mfrsById, name) => {
      // Sort by manufacturer_id for a stable label -- row order from the API
      // isn't guaranteed across requests, so picking "first" without an
      // explicit tie-break would make the chip text flicker between loads.
      const names = Array.from(mfrsById.entries())
        .sort(([idA], [idB]) => idA - idB)
        .map(([, mfrName]) => mfrName);
      map.set(
        name,
        names.length > 1 ? `${names[0]}, +${names.length - 1} more` : names[0]
      );
    });
    return map;
  }, [rows]);

  // Rule 5: Deduplicate leaf rows that are identical across all currently visible
  // (non-grouping) data columns. The dedup key is the visible data-column values
  // UNIONED with the active grouping dimensions -- Rule 5 is scoped to leaf rows
  // within a group branch (docs/features/datagrid-improvements.md), not across
  // branches, so two rows differing only in a grouping dimension (e.g.
  // manufacturer_name) must never collapse just because that column is hidden
  // as a flat column while it's being used for grouping. Uses raw
  // columnVisibilityState (not the grouping-merged columnVisibilityModel) --
  // see getDedupFieldKeys for the full rationale.
  const deduplicatedRows = useMemo(
    () =>
      deduplicateRows(
        rows,
        getDedupFieldKeys(columnVisibilityState, groupingModel)
      ),
    [rows, columnVisibilityState, groupingModel]
  );

  // Compute the set of group keys that have exactly one child.
  // KEY TIMING FIX (AD-2): Computed synchronously during render into a useRef so that
  // isGroupExpandedByDefault — which MUI may call inside its post-render tree
  // initialization — always reads the current Set via ref.current. A useMemo would
  // produce a stale closure if MUI captures the callback before the memo re-runs.
  //
  // Key format: '{field1}/{value1}-{field2}/{value2}' — matches MUI auto-generated row ID suffix
  // Computed from deduplicatedRows so the keys match what MUI sees.
  //
  // GROUPING VALUE GETTER FIX: The preferred_product_name column defines a groupingValueGetter
  // that falls back to product_cas_name when preferred_product_name is null. MUI uses this
  // resolved value for grouping keys and auto-generated row IDs. We must pass the same resolved
  // values to computeCollapsingKeys so its generated keys match MUI's row IDs exactly.
  const rowsForCollapsing = deduplicatedRows.map(row => ({
    ...row,
    preferred_product_name: getProductDisplayName(row),
  }));
  const singleChildGroupKeysRef = useRef<Set<string>>(new Set());
  // Pure computation extracted to collapsingUtils.ts for unit testability (AD-2).
  const { singleChildGroupKeys: _computedSCGK } = computeCollapsingKeys(
    rowsForCollapsing as Record<string, unknown>[],
    groupingModel
  );
  singleChildGroupKeysRef.current = _computedSCGK;
  // Stable local alias for use in render-time callbacks and effects
  const singleChildGroupKeys = singleChildGroupKeysRef.current;

  // Group-chain key -> representative row, for groups whose descendant leaves
  // all share one product_id (see actionsColumn.getActions). Same ref-based
  // timing rationale as singleChildGroupKeysRef above.
  const singleProductGroupRowsRef = useRef<
    Map<string, Record<string, unknown>>
  >(new Map());
  singleProductGroupRowsRef.current = computeSingleProductGroupRows(
    rowsForCollapsing as Record<string, unknown>[],
    groupingModel
  );
  const shouldAutoExpand = computeShouldAutoExpand(
    rowsForCollapsing as Record<string, unknown>[]
  );

  // MUI's rowSpanning feature does not actually merge cells in this grid
  // (verified empirically -- it never produces a spanned cell here, with or
  // without pinning/custom row heights), so a single set of actions per
  // product across the WHOLE grid is achieved instead by picking exactly one
  // winning row per product_id and suppressing every other row tied to that
  // product -- see actionsColumn.getActions/cellClassName.
  //
  // A product can legitimately appear under multiple disjoint branches
  // depending on the active grouping fields and their order (e.g. sold by
  // two different manufacturers, or spread across facility statuses when
  // that's the first grouping level) -- each such branch can independently
  // form a valid "this branch's leaves are all one product" node. Only ONE
  // of those may ever show actions, so the winner is simply the product's
  // FIRST occurrence in deduplicatedRows order: the shallowest visible
  // (non single-child-hidden) node in THAT occurrence's own chain, or the
  // leaf itself when no such node exists (e.g. a single-row product, or a
  // row whose grouping-relevant field is null and falls out of that level
  // entirely -- see collapsingUtils' null handling).
  groupingModelRef.current = groupingModel;
  const computeOwnHeaderKey = (
    row: Record<string, unknown>,
    productId: number
  ): string | undefined => {
    const effectiveFields = groupingModelRef.current.filter(
      f => row[f] != null
    );
    let key = '';
    for (let d = 0; d < effectiveFields.length; d++) {
      const field = effectiveFields[d];
      key = key
        ? `${key}-${field}/${String(row[field])}`
        : `${field}/${String(row[field])}`;
      if (singleChildGroupKeysRef.current.has(key)) continue; // hidden
      const rep = singleProductGroupRowsRef.current.get(key);
      if (rep && (rep['product_id'] as number | undefined) === productId) {
        return key;
      }
    }
    return undefined;
  };

  const winningActionRef = useRef<
    Map<number, { type: 'header'; key: string } | { type: 'leaf'; id: string }>
  >(new Map());
  const winningHeaderKeysRef = useRef<Set<string>>(new Set());
  {
    const winning = new Map<
      number,
      { type: 'header'; key: string } | { type: 'leaf'; id: string }
    >();
    rowsForCollapsing.forEach(row => {
      const productId = row.product_id;
      if (productId == null || winning.has(productId)) return;
      const headerKey = computeOwnHeaderKey(
        row as Record<string, unknown>,
        productId
      );
      winning.set(
        productId,
        headerKey
          ? { type: 'header', key: headerKey }
          : { type: 'leaf', id: row.id }
      );
    });
    winningActionRef.current = winning;
    const headerKeys = new Set<string>();
    winning.forEach(v => {
      if (v.type === 'header') headerKeys.add(v.key);
    });
    winningHeaderKeysRef.current = headerKeys;
  }

  // A product's winning row (header or leaf) always has its Actions-column
  // border removed -- for a header that's unconditionally safe (a group
  // always has a child rendered directly below it), but a winning LEAF only
  // has more of the same product below it when its BRANCH has other
  // physical rows too (e.g. flat sibling rows under a single grouping
  // level, with no header ever forming).
  //
  // This must be scoped per BRANCH ("zone"), not globally per product_id:
  // when a product spans multiple branches (e.g. two manufacturers), each
  // branch needs its OWN last-row-keeps-border boundary, independent of
  // which branch happens to hold the single global action winner. Using a
  // global per-product "last row" here would remove the border on the tail
  // of every branch except whichever one is physically last overall,
  // silently deleting the boundary between an earlier branch's last row and
  // the next, unrelated node that follows it.
  //
  // A zone is identified by computeOwnHeaderKey's result when the branch has
  // one (all rows in that subtree share it), or by the row's own full chain
  // + product_id when it doesn't (the flat-sibling case, where "the zone" is
  // just every row sharing that exact chain).
  const isZoneLastRowRef = useRef<Set<string>>(new Set());
  {
    const lastRowIdByZone = new Map<string, string>();
    rowsForCollapsing.forEach(row => {
      const productId = row.product_id;
      if (productId == null) return;
      const rowRecord = row as Record<string, unknown>;
      const headerKey = computeOwnHeaderKey(rowRecord, productId);
      const zone = headerKey
        ? `h:${headerKey}`
        : `l:${groupingModelRef.current
            .filter(f => rowRecord[f] != null)
            .map(f => `${f}/${String(rowRecord[f])}`)
            .join('-')}::${productId}`;
      lastRowIdByZone.set(zone, row.id);
    });
    isZoneLastRowRef.current = new Set(lastRowIdByZone.values());
  }

  // Return 0-height for single-child group rows so they are visually hidden.
  // All depths including depth-0 are hidden when single-child; the first visible
  // descendant group row renders the product name inline in the MPN column.
  //
  // HEIGHT CACHE FIX: MUI caches row heights by ID. When data changes (filter switch,
  // page change) group row IDs can persist with stale cached heights. Using the ref
  // (singleChildGroupKeysRef.current) instead of a closure value ensures this callback
  // always reads the latest computed set, even after resetRowHeights() forces re-evaluation.
  const getRowHeight = useCallback(
    (params: GridRowHeightParams): number | null | undefined | 'auto' => {
      const { id } = params;
      if (typeof id === 'string' && id.startsWith('auto-generated-row-')) {
        const key = id.slice('auto-generated-row-'.length);
        if (singleChildGroupKeysRef.current.has(key)) return 0;
      }
      // 'auto' (not the fixed density height) — multi-value array columns
      // (synonyms, manufacturer, orange_book_ingredient, etc.) render via
      // ExpandableListCell, which wraps its items onto multiple lines once
      // they don't fit one row at the column's width. A fixed row height
      // clips that wrapped content (items 3+ and the "+N more" badge become
      // invisible, not just visually truncated) — 'auto' measures each row's
      // actual rendered content instead.
      return 'auto';
    },
    [] // stable — reads singleChildGroupKeysRef.current which is always synchronized during render
  );

  // CSS class applied to hidden group rows so we can enforce overflow:hidden
  // (height:0 alone doesn't clip cell content in all browsers).
  const getRowClassName = useCallback(
    (params: { id: string | number }): string => {
      const { id } = params;
      if (typeof id === 'string' && id.startsWith('auto-generated-row-')) {
        const key = id.slice('auto-generated-row-'.length);
        if (singleChildGroupKeysRef.current.has(key))
          return 'lgm-hidden-group-row';
      }
      return '';
    },
    [] // stable — reads singleChildGroupKeysRef.current which is always synchronized during render
  );

  // Expand single-child group rows at tree creation time.
  // When shouldAutoExpand is true, also expands all other groups (≤10 distinct products).
  // Reads singleChildGroupKeysRef.current so the callback is never stale even if MUI
  // captures it before the memo/state cycle that created the Set completes.
  const isGroupExpandedByDefault = useCallback(
    (node: GridGroupNode): boolean => {
      const nodeId = node.id;
      if (
        typeof nodeId === 'string' &&
        nodeId.startsWith('auto-generated-row-')
      ) {
        const key = nodeId.slice('auto-generated-row-'.length);
        // Single-child hidden groups are always expanded (zero-height, so invisible)
        if (singleChildGroupKeysRef.current.has(key)) return true;
      }
      // Auto-expand: expand all visible groups for small result sets
      return shouldAutoExpand;
    },
    [shouldAutoExpand] // singleChildGroupKeysRef is a ref — never stale; only shouldAutoExpand needed
  );

  // Programmatic expansion: fires on filter/grouping changes after initial render.
  // Guards:
  //   1. Checks childrenExpanded before calling (no-op if already expanded).
  //   2. Checks parent chain — only expands if all ancestors are expanded.
  //      Without this guard, expanding a hidden-group node whose parent is user-collapsed
  //      causes MUI to re-render the node's children as visible (despite the parent being closed).
  useEffect(() => {
    if (!apiRef.current) return;

    const isAncestorChainExpanded = (nodeId: string | number): boolean => {
      if (!apiRef.current) return false;
      const node = gridRowNodeSelector(apiRef, nodeId) as GridGroupNode | null;
      if (!node) return false;
      if (node.parent === null) return true; // root
      const parentNode = gridRowNodeSelector(
        apiRef,
        node.parent
      ) as GridGroupNode | null;
      if (!parentNode) return false;
      if (parentNode.type === 'group' && !parentNode.childrenExpanded)
        return false;
      return isAncestorChainExpanded(node.parent);
    };

    // DEPTH-ORDER FIX (AD-2): Sort keys by path-segment depth (ascending) so that
    // parent nodes are always expanded before their children. Without this,
    // isAncestorChainExpanded sees unexpanded parents mid-iteration and skips children.
    const sortedKeys = Array.from(singleChildGroupKeys).sort(
      (a, b) => a.split('-').length - b.split('-').length
    );

    sortedKeys.forEach(key => {
      const rowId = `auto-generated-row-${key}`;
      try {
        const node = gridRowNodeSelector(apiRef, rowId) as GridGroupNode | null;
        if (
          node &&
          node.type === 'group' &&
          !node.childrenExpanded &&
          isAncestorChainExpanded(rowId)
        ) {
          apiRef.current?.setRowChildrenExpansion(rowId, true);
        }
      } catch {
        // Node not yet in tree — ignore
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- apiRef is a stable React ref (useGridApiRef); safe to omit
  }, [singleChildGroupKeys, deduplicatedRows]);

  // HEIGHT CACHE FIX: Invalidate MUI's per-row height cache whenever the page data
  // changes (filter switch, page change, sort change). MUI caches heights by row ID;
  // group rows that persist across data changes (same auto-generated ID) retain their
  // old cached height. resetRowHeights() forces MUI to re-call getRowHeight for every
  // row, picking up the current singleChildGroupKeysRef.current set.
  // Must run in an effect (after render) so apiRef is mounted and the ref is fresh.
  useEffect(() => {
    if (!apiRef.current) return;
    apiRef.current.resetRowHeights();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- apiRef is a stable React ref (useGridApiRef); safe to omit
  }, [deduplicatedRows]);

  // Custom grouping column cell renderer:
  // - GROUP rows: hidden groups (singleChildGroupKeys) render null; visible groups show own value with chevron; all other columns blank
  // - LEAF rows: all grouping columns blank
  const groupingColDef = useMemo(
    () => ({
      renderCell: (params: GridRenderCellParams) => {
        const { rowNode, colDef } = params;

        // Extract the actual field name from the grouping column field
        // Format: __row_group_by_columns_group_FIELDNAME__
        const colGroupingField = stripGroupingPrefix(colDef.field);

        if (rowNode.type === 'group') {
          const groupNode = rowNode as GridGroupNode;

          // Hidden group row (height 0) — render null to avoid layout artifacts.
          // All single-child groups at any depth are hidden, including depth-0.
          const nodeId = groupNode.id;
          if (
            typeof nodeId === 'string' &&
            nodeId.startsWith('auto-generated-row-')
          ) {
            const key = nodeId.slice('auto-generated-row-'.length);
            if (singleChildGroupKeys.has(key)) return null;
          }

          // Group's own column — render with chevron.
          if (colGroupingField === groupNode.groupingField) {
            // For preferred_product_name groups, pass source so the indicator
            // renders on just the text span inside GroupRowCell (not the whole cell).
            const displayNameSource =
              colGroupingField === 'preferred_product_name'
                ? (preferredNameSourceMap.get(
                    groupNode.groupingKey as string
                  ) ?? null)
                : null;
            const sourceLabel =
              displayNameSource === DISPLAY_NAME_SOURCE.MANUFACTURER
                ? (preferredNameManufacturerMap.get(
                    groupNode.groupingKey as string
                  ) ?? undefined)
                : undefined;
            return (
              <GroupRowCell
                params={params}
                displayNameSource={displayNameSource}
                sourceLabel={sourceLabel}
              />
            );
          }

          // Ancestor column — render the ancestor's value inline when that ancestor
          // group is hidden (single-child collapsed). Walk up the parent chain to
          // the ancestor whose OWN groupingField matches this column's field, and
          // check membership in singleChildGroupKeys. This implements Rule 1
          // pass-through: a hidden group's value must appear on its first visible
          // descendant row (OQ-12 fix).
          //
          // Matched by FIELD, not by raw tree depth (`groupingModel.indexOf`) —
          // MUI drops a grouping level from the tree entirely for rows where that
          // field is null, so a sibling branch that skipped a level can have a
          // DIFFERENT field sitting at the same raw depth. A depth-index lookup
          // would silently render that field's value under the wrong column
          // header (see findAncestorNodeByField's doc comment in collapsingUtils.ts).
          if (groupingModel.includes(colGroupingField)) {
            const cursor = findAncestorNodeByField(
              groupNode,
              colGroupingField,
              id =>
                apiRef.current
                  ? (gridRowNodeSelector(apiRef, id) as GridGroupNode | null)
                  : null
            );
            if (cursor) {
              const ancestorId = cursor.id;
              if (
                typeof ancestorId === 'string' &&
                ancestorId.startsWith('auto-generated-row-')
              ) {
                const ancestorKey = ancestorId.slice(
                  'auto-generated-row-'.length
                );
                if (singleChildGroupKeys.has(ancestorKey)) {
                  const ancestorSpan = (
                    <span style={{ marginLeft: '4px' }}>
                      {formatGroupingKey(colGroupingField, cursor.groupingKey)}
                    </span>
                  );
                  if (colGroupingField === 'preferred_product_name') {
                    const ancestorSource =
                      preferredNameSourceMap.get(
                        cursor.groupingKey as string
                      ) ?? null;
                    const ancestorMfrLabel =
                      ancestorSource === DISPLAY_NAME_SOURCE.MANUFACTURER
                        ? (preferredNameManufacturerMap.get(
                            cursor.groupingKey as string
                          ) ?? undefined)
                        : undefined;
                    return (
                      <DisplayNameSourceIndicator
                        displayNameSource={ancestorSource}
                        sourceLabel={ancestorMfrLabel}>
                        {ancestorSpan}
                      </DisplayNameSourceIndicator>
                    );
                  }
                  return ancestorSpan;
                }
              }
            }
          }

          return null;
        }

        // Leaf rows: render a hidden ancestor's value inline when this leaf is the
        // first visible entity below that ancestor. Conditions for inline rendering:
        //   (a) the ancestor group for colGroupingField is in singleChildGroupKeys, AND
        //   (b) every group between that ancestor and this leaf is also hidden
        //       (i.e. no visible group row above the leaf has already shown the value).
        // This covers both single-leaf products (all ancestors hidden) and leaves under
        // a visible group where only the immediate parent is hidden (OQ-12 fix).
        if (rowNode.type === 'leaf') {
          const colModelIndex = groupingModel.indexOf(colGroupingField);
          if (colModelIndex >= 0) {
            const r = params.row as Record<string, unknown>;
            const effectiveFields = groupingModel.filter(f => r[f] != null);
            const effectiveIndex = effectiveFields.indexOf(colGroupingField);
            if (effectiveIndex >= 0) {
              const ancestorKey = effectiveFields
                .slice(0, effectiveIndex + 1)
                .map(f => `${f}/${String(r[f])}`)
                .join('-');
              if (singleChildGroupKeys.has(ancestorKey)) {
                // Only render inline if every group between this ancestor and the
                // leaf is also hidden (i.e., there is no visible group row above
                // this leaf that has already shown the ancestor's value).
                const hasVisibleDescendantGroup = effectiveFields
                  .slice(effectiveIndex + 1)
                  .some((_, di) => {
                    const descKey = effectiveFields
                      .slice(0, effectiveIndex + 2 + di)
                      .map(f => `${f}/${String(r[f])}`)
                      .join('-');
                    return !singleChildGroupKeys.has(descKey);
                  });
                if (!hasVisibleDescendantGroup) {
                  const leafValue = formatGroupingKey(
                    colGroupingField,
                    r[colGroupingField]
                  );
                  const leafSpan = (
                    <span style={{ marginLeft: '4px' }}>{leafValue}</span>
                  );
                  if (colGroupingField === 'preferred_product_name') {
                    const source =
                      (r.display_name_source as DisplayNameSource) ?? null;
                    const mfrLabel =
                      source === DISPLAY_NAME_SOURCE.MANUFACTURER
                        ? ((r.manufacturer_name as string | undefined) ??
                          undefined)
                        : undefined;
                    return (
                      <DisplayNameSourceIndicator
                        displayNameSource={source}
                        sourceLabel={mfrLabel}>
                        {leafSpan}
                      </DisplayNameSourceIndicator>
                    );
                  }
                  return leafSpan;
                }
              }
            }
          }
        }

        // All other cells: blank.
        return null;
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- apiRef is a stable React ref
    [
      singleChildGroupKeys,
      groupingModel,
      stripGroupingPrefix,
      preferredNameSourceMap,
      preferredNameManufacturerMap,
    ]
  );

  /**
   * Debug export — downloads a plain-text file describing the full grid state:
   * active filter preset + rules, grouping model, visible columns, sort/pagination,
   * and every row with its type (GROUP/LEAF), value, chevron presence, and expand state.
   * Hidden single-child group rows are labelled [HIDDEN:single-child] with no-chevron.
   */
  const handleDebugExport = useCallback(() => {
    if (!apiRef.current) return;

    const lines: string[] = [];
    const now = new Date().toISOString();

    lines.push('=== LGM Products DataGrid Debug Export ===');
    lines.push(`Generated: ${now}`);
    lines.push('Data source: Real API');
    lines.push('');

    // --- Filter state ---
    lines.push('=== FILTER STATE ===');
    lines.push(`Search mode: ${searchState.mode}`);
    if (
      searchState.mode === 'advanced' &&
      searchState.advancedQuery?.filterGroups?.length
    ) {
      lines.push('Advanced search rules:');
      searchState.advancedQuery.filterGroups.forEach((group, gi) => {
        lines.push(`  Group ${gi + 1} (${group.logicalOperator}):`);
        group.rules.forEach(rule => {
          const val = rule.value != null ? `"${rule.value}"` : '(empty)';
          lines.push(`    ${rule.field} ${rule.operator} ${val}`);
        });
      });
    } else {
      lines.push('Advanced search rules: (none)');
    }
    if (filterModel.items.length > 0) {
      lines.push('Column filters:');
      filterModel.items.forEach(item => {
        const val = item.value != null ? `"${item.value}"` : '(empty)';
        lines.push(`  ${item.field} ${item.operator} ${val}`);
      });
    } else {
      lines.push('Column filters: (none)');
    }
    if (searchState.basicTerm) {
      lines.push(`Search term: "${searchState.basicTerm}"`);
    }
    lines.push('');

    // --- Grouping state ---
    lines.push('=== GROUPING STATE ===');
    let groupingPresetLabel = 'Custom';
    if (Object.prototype.hasOwnProperty.call(DEFAULT_PATTERNS, activePreset)) {
      groupingPresetLabel =
        DEFAULT_PATTERNS[activePreset as keyof typeof DEFAULT_PATTERNS]
          .shortLabel;
    } else if (activePreset.startsWith('custom_')) {
      const name = activePreset.substring(7);
      groupingPresetLabel =
        customPresets.find(p => p.name === name)?.name ?? 'Custom';
    }
    lines.push(`Active preset: ${groupingPresetLabel}`);
    lines.push(
      `Grouping model: ${groupingModel
        .map(f => GROUPABLE_DIMENSIONS.find(d => d.field === f)?.label ?? f)
        .join(' → ')}`
    );
    lines.push('');

    // --- Grid state ---
    lines.push('=== GRID STATE ===');
    const totalRows = apiTotalProducts || 0;
    const totalPages = Math.ceil(totalRows / paginationModel.pageSize) || 1;
    lines.push(
      `Page: ${paginationModel.page + 1} of ${totalPages}  (page size: ${paginationModel.pageSize})`
    );
    lines.push(`Total rows: ${totalRows}`);
    if (sortModel.length > 0) {
      lines.push(
        `Sort: ${sortModel[0].field} ${(sortModel[0].sort ?? 'asc').toUpperCase()}`
      );
    }
    const visibleCols = columns
      .filter(
        col =>
          columnVisibilityModel[col.field] !== false && col.field !== 'actions'
      )
      .map(col => col.headerName || col.field);
    lines.push(
      `Visible columns (${visibleCols.length}): ${visibleCols.join(', ')}`
    );
    lines.push('');

    // --- Single-child group keys ---
    lines.push('=== SINGLE-CHILD KEYS ===');
    lines.push(
      `Total single-child group keys: ${singleChildGroupKeysRef.current.size}`
    );
    if (singleChildGroupKeysRef.current.size > 0) {
      // Sort by depth ascending (same order as useEffect expansion) for readability
      Array.from(singleChildGroupKeysRef.current)
        .sort((a, b) => a.split('-').length - b.split('-').length)
        .forEach(key => lines.push(`  ${key}`));
    } else {
      lines.push('  (none)');
    }
    lines.push('');

    // --- Rows ---
    lines.push('=== ROWS ===');
    let rowIds: (string | number)[];
    try {
      rowIds = apiRef.current.getSortedRowIds();
    } catch {
      rowIds = apiRef.current.getAllRowIds();
    }

    const dataColDefs = columns.filter(
      col =>
        columnVisibilityModel[col.field] !== false && col.field !== 'actions'
    );

    let leafCount = 0;
    rowIds.forEach(rowId => {
      const node = apiRef.current ? gridRowNodeSelector(apiRef, rowId) : null;
      if (!node) return;

      const indent = '  '.repeat(node.depth);

      if (node.type === 'group') {
        const groupNode = node as GridGroupNode;
        const key =
          typeof rowId === 'string' && rowId.startsWith('auto-generated-row-')
            ? rowId.slice('auto-generated-row-'.length)
            : '';
        const isHidden = key !== '' && singleChildGroupKeysRef.current.has(key);
        const expandedStr = groupNode.childrenExpanded
          ? 'expanded'
          : 'collapsed';
        const chevronStr = isHidden ? 'no-chevron' : `chevron:${expandedStr}`;
        const hiddenTag = isHidden ? ' [HIDDEN:single-child]' : '';
        lines.push(
          `${indent}[GROUP${hiddenTag}] ${groupNode.groupingField}="${groupNode.groupingKey ?? '(No value)'}" [${expandedStr}] [${chevronStr}] depth:${node.depth}`
        );
      } else if (node.type === 'leaf') {
        leafCount += 1;
        const rowData = apiRef.current?.getRow(rowId) as Record<
          string,
          unknown
        > | null;
        if (!rowData) {
          lines.push(`${indent}[LEAF] (no data) id=${rowId}`);
          return;
        }
        const cellValues = dataColDefs
          .map(col => {
            const value = rowData[col.field];
            return `${col.field}=${value != null ? `"${String(value)}"` : 'null'}`;
          })
          .join(' | ');
        // Always include the composite row ID so apparent duplicates can be distinguished
        const compositeId =
          rowData['id'] != null ? String(rowData['id']) : String(rowId);
        lines.push(`${indent}[LEAF] id=${compositeId} | ${cellValues}`);
      }
    });

    lines.push('');
    let hiddenGroupCount = 0;
    rowIds.forEach(rowId => {
      if (typeof rowId !== 'string' || !rowId.startsWith('auto-generated-row-'))
        return;
      const key = rowId.slice('auto-generated-row-'.length);
      if (singleChildGroupKeysRef.current.has(key)) hiddenGroupCount += 1;
    });
    lines.push(`Total hidden group rows on this page: ${hiddenGroupCount}`);
    lines.push(`Total leaf rows on this page: ${leafCount}`);

    // Trigger download
    const blob = new Blob([lines.join('\n')], {
      type: 'text/plain;charset=utf-8',
    });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `lgm-debug-${now.replace(/[:.]/g, '-')}.txt`;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- singleChildGroupKeys omitted (reads singleChildGroupKeysRef.current, always current); apiRef is a stable MutableRefObject
  }, [
    searchState,
    filterModel,
    activePreset,
    customPresets,
    groupingModel,
    paginationModel,
    apiTotalProducts,
    rows,
    deduplicatedRows,
    columns,
    columnVisibilityModel,
    sortModel,
  ]);

  return (
    <div style={styles.container}>
      {/* Unified Search — basic text, advanced filter panel, preset management */}
      <div
        style={{
          flexShrink: 0,
          borderBottom: `1px solid ${GRAY_300}`,
          paddingBottom: '8px',
        }}>
        <UnifiedSearchBar
          fieldConfigs={PRODUCTS_FIELD_CONFIGS}
          placeholder="Search products, CAS numbers, manufacturers, synonyms..."
          builtInPresets={PRODUCTS_UNIFIED_PRESETS}
          storageKeyUserPresets={STORAGE_KEY_FILTER_PRESETS}
          storageKeyActivePreset={STORAGE_KEY_FILTER_ACTIVE}
          defaultPreset={PRODUCTS_DEFAULT_PRESET}
          initialSearchState={
            initialManufacturerFilter ? searchState : undefined
          }
          onSearchChange={handleSearchChange}
          actions={
            <>
              {/* Grouping dropdown */}
              <Button
                size="small"
                variant="outlined"
                onClick={e => setGroupingMenuAnchorEl(e.currentTarget)}
                endIcon={<ChevronDown size={14} />}
                aria-label="Grouping options"
                aria-haspopup="true"
                style={{
                  whiteSpace: 'nowrap',
                  textTransform: 'none',
                  fontSize: '0.8125rem',
                }}>
                Grouping: {getGroupingLabel()}
              </Button>
              <Menu
                anchorEl={groupingMenuAnchorEl}
                open={Boolean(groupingMenuAnchorEl)}
                onClose={() => setGroupingMenuAnchorEl(null)}>
                {(
                  Object.keys(DEFAULT_PATTERNS) as Array<
                    keyof typeof DEFAULT_PATTERNS
                  >
                ).map(key => (
                  <MenuItem
                    key={key}
                    selected={activePreset === key}
                    onClick={() => {
                      handleApplyPreset(key);
                      setGroupingMenuAnchorEl(null);
                    }}>
                    🔒 {DEFAULT_PATTERNS[key].shortLabel}
                    {DEFAULT_PATTERNS[key].model.length > 0 &&
                      ` — ${DEFAULT_PATTERNS[key].model
                        .map(
                          f =>
                            GROUPABLE_DIMENSIONS.find(d => d.field === f)
                              ?.label ?? f
                        )
                        .join(' → ')}`}
                  </MenuItem>
                ))}
                {customPresets.length > 0 && <Divider />}
                {customPresets.map(preset => (
                  <MenuItem
                    key={preset.name}
                    selected={activePreset === `custom_${preset.name}`}
                    onClick={() => {
                      handleLoadCustomPreset(preset);
                      setGroupingMenuAnchorEl(null);
                    }}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      minWidth: 280,
                    }}>
                    <span>{preset.name}</span>
                    <span
                      role="button"
                      tabIndex={-1}
                      aria-label={`Delete preset "${preset.name}"`}
                      onClick={e => {
                        e.stopPropagation();
                        handleDeleteCustomPreset(preset.name);
                      }}
                      style={{
                        marginLeft: '1.5rem',
                        cursor: 'pointer',
                        color: GRAY_400,
                      }}>
                      🗑
                    </span>
                  </MenuItem>
                ))}
                <Divider />
                <MenuItem
                  onClick={() => {
                    setGroupingMenuAnchorEl(null);
                    handleOpenGroupingModal();
                  }}>
                  ⚙️ Configure grouping…
                </MenuItem>
              </Menu>

              {/* Export progress indicator — shown inline while in-flight */}
              {exportError && (
                <div style={styles.exportErrorBanner}>
                  <span style={styles.exportErrorText}>{exportError}</span>
                  <button
                    onClick={() => {
                      setExportError(null);
                      setExportState('idle');
                    }}
                    style={styles.exportErrorDismiss}
                    aria-label="Dismiss export error"
                    title="Dismiss">
                    ×
                  </button>
                </div>
              )}
              {exportState === 'exporting' && (
                <div style={styles.exportProgressGroup}>
                  <CircularProgress size={14} thickness={5} />
                  <button
                    type="button"
                    onClick={handleCancelExport}
                    title="Cancel export"
                    aria-label="Cancel export"
                    style={styles.exportCancelIconButton}>
                    ×
                  </button>
                </div>
              )}
              {exportState === 'cancelling' && (
                <CircularProgress size={14} thickness={5} />
              )}

              {/* Overflow menu — Upload, Download Excel, Debug */}
              <IconButton
                size="small"
                onClick={e => setOverflowMenuAnchorEl(e.currentTarget)}
                aria-label="More actions"
                aria-haspopup="true"
                title="More actions"
                data-testid="overflow-menu-button">
                <MoreVertical size={16} />
              </IconButton>
              <Menu
                anchorEl={overflowMenuAnchorEl}
                open={Boolean(overflowMenuAnchorEl)}
                onClose={() => setOverflowMenuAnchorEl(null)}>
                {onUpload && (
                  <MenuItem
                    onClick={() => {
                      onUpload();
                      setOverflowMenuAnchorEl(null);
                    }}>
                    <Upload size={14} style={{ marginRight: '0.5rem' }} />
                    Upload Product Sheet
                  </MenuItem>
                )}
                <MenuItem
                  onClick={() => {
                    handleExcelExport();
                    setOverflowMenuAnchorEl(null);
                  }}
                  disabled={
                    exportState === 'exporting' || exportState === 'cancelling'
                  }
                  data-testid="overflow-download-excel">
                  <Download size={14} style={{ marginRight: '0.5rem' }} />
                  {exportState === 'exporting' || exportState === 'cancelling'
                    ? 'Exporting…'
                    : 'Download Excel'}
                </MenuItem>
                {(process.env.REACT_APP_ENV === 'stage' ||
                  process.env.REACT_APP_ENV === 'development') && (
                  <MenuItem
                    onClick={() => {
                      handleDebugExport();
                      setOverflowMenuAnchorEl(null);
                    }}
                    data-testid="overflow-debug-report">
                    <Bug size={14} style={{ marginRight: '0.5rem' }} />
                    Debug Report
                  </MenuItem>
                )}
                {isAdmin && isValidateProductsDevtoolEnabled && (
                  <MenuItem
                    onClick={() => {
                      setIsValidateProductsModalOpen(true);
                      setOverflowMenuAnchorEl(null);
                    }}
                    data-testid="overflow-validate-products">
                    <ShieldCheck size={14} style={{ marginRight: '0.5rem' }} />
                    Validate Products
                  </MenuItem>
                )}
                <Divider />
                <MenuItem
                  onClick={() => {
                    setDensity(density === 'standard' ? 'compact' : 'standard');
                    setOverflowMenuAnchorEl(null);
                  }}>
                  {density === 'standard' ? (
                    <List size={14} style={{ marginRight: '0.5rem' }} />
                  ) : (
                    <AlignJustify size={14} style={{ marginRight: '0.5rem' }} />
                  )}
                  {density === 'standard' ? 'Compact Rows' : 'Standard Rows'}
                </MenuItem>
                <Divider />
                <MenuItem
                  onClick={handleResetToDefaults}
                  data-testid="overflow-reset-to-defaults">
                  <RotateCcw size={14} style={{ marginRight: '0.5rem' }} />
                  Reset to Defaults
                </MenuItem>
              </Menu>
            </>
          }
        />
      </div>

      {/* Validate Products Modal (issue #637, VPE-009) */}
      {isAdmin && isValidateProductsDevtoolEnabled && (
        <ValidateProductsModal
          open={isValidateProductsModalOpen}
          onClose={() => setIsValidateProductsModalOpen(false)}
          filterParams={{
            search: apiParams.search,
            advancedSearch: apiParams.advancedSearch,
          }}
        />
      )}

      {/* Configure Grouping Modal */}
      <Dialog
        open={isGroupingModalOpen}
        onClose={handleCloseGroupingModal}
        maxWidth="sm"
        fullWidth>
        <DialogTitle>Configure Grouping</DialogTitle>
        <DialogContent>
          <GroupingControls
            groupingModel={pendingGroupingModel}
            onGroupingModelChange={setPendingGroupingModel}
            availableDimensions={GROUPABLE_DIMENSIONS}
            onSavePreset={handleSaveAsPreset}
            existingPresetNames={customPresets.map(p => p.name)}
          />
        </DialogContent>
        <DialogActions>
          <LgmButton variant="flat" onClick={handleCloseGroupingModal}>
            Cancel
          </LgmButton>
          <LgmButton variant="contained" onClick={handleApplyGroupingModal}>
            Apply
          </LgmButton>
        </DialogActions>
      </Dialog>

      {/* DataGrid */}
      <div style={styles.gridContainer}>
        {/* Show error message if API request failed */}
        {apiError && (
          <div
            style={{
              padding: '1rem',
              backgroundColor: '#fee',
              color: '#c00',
              borderRadius: '4px',
              marginBottom: '1rem',
            }}>
            Failed to load products data. Please try again.
          </div>
        )}
        <DataGridPremium
          rows={deduplicatedRows}
          columns={columns}
          loading={apiLoading}
          // Row count — only valid with paginationMode="server"
          rowCount={apiTotalProducts || 0}
          // Key configuration for row grouping
          rowGroupingColumnMode="multiple" // Each group level gets its own column
          rowGroupingModel={groupingModel} // Use controlled state
          onRowGroupingModelChange={handleGroupingModelChange}
          defaultGroupingExpansionDepth={0} // Start collapsed
          getRowHeight={getRowHeight}
          getRowClassName={getRowClassName}
          apiRef={apiRef}
          isGroupExpandedByDefault={isGroupExpandedByDefault}
          groupingColDef={groupingColDef}
          // Column visibility based on grouping + user preference
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
          // Column order — restored from sessionStorage, or the Sourcing-led
          // default, on mount
          initialState={{
            columns: { orderedFields: initialColumnOrder },
            ...(hasActions && { pinnedColumns: { right: ['actions'] } }),
          }}
          onColumnOrderChange={handleColumnOrderChange}
          // Filtering (server-side)
          filterMode="server"
          filterModel={filterModel}
          onFilterModelChange={model => setFilterModel(model)}
          // Pagination (server-side)
          pagination
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={model => {
            setPaginationModel(model);
          }}
          pageSizeOptions={[10, 25, 50, 100]}
          // Sorting (server-side)
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={model => setSortModel(model)}
          sortingOrder={['asc', 'desc']}
          // UI
          density={density}
          disableRowSelectionOnClick
          // Performance
          columnBufferPx={100}
          rowBufferPx={100}
          // Styling
          sx={{
            ...dataGridStyles,
            // Fully hide single-child group rows: height:0 alone does not clip
            // cell content in all browsers, so overflow:hidden is required.
            '& .lgm-hidden-group-row': {
              overflow: 'hidden',
              visibility: 'hidden',
              pointerEvents: 'none',
              minHeight: '0 !important',
              maxHeight: '0 !important',
            },
            // See actionsColumn.cellClassName: rows within a product's
            // header-owned subtree render nothing here except the header,
            // so their cell border is dropped for a seamless merged look.
            '& .lgm-actions-cell-noborder': {
              borderBottom: 'none',
            },
          }}
        />
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    minHeight: 0,
  },
  searchContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    marginBottom: '1rem',
  },
  searchInputWrapper: {
    position: 'relative',
    flex: 1,
  },
  searchInput: {
    width: '100%',
    boxSizing: 'border-box',
    padding: '0.625rem 2.5rem 0.625rem 1rem',
    fontSize: '0.875rem',
    border: `1px solid ${GRAY_300}`,
    borderRadius: '0.375rem',
    outline: 'none',
    transition: 'border-color 0.15s',
  },
  searchClearButton: {
    position: 'absolute',
    right: '8px',
    top: '50%',
    transform: 'translateY(-50%)',
    padding: '0.25rem',
    fontSize: '1.25rem',
    fontWeight: 700,
    border: 'none',
    background: 'transparent',
    color: GRAY_400,
    cursor: 'pointer',
    lineHeight: 1,
  },
  searchResultCount: {
    fontSize: '0.75rem',
    color: GRAY_500,
    fontWeight: 500,
    whiteSpace: 'nowrap',
    marginLeft: '-0.25rem',
  },
  exportButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0.375rem',
    border: `1px solid ${GRAY_300}`,
    borderRadius: '0.375rem',
    background: 'white',
    color: GRAY_700,
    cursor: 'pointer',
    flexShrink: 0,
  },
  exportErrorBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.375rem 0.625rem',
    backgroundColor: '#fef2f2',
    border: '1px solid #fca5a5',
    borderRadius: '0.375rem',
    maxWidth: '340px',
  },
  exportErrorText: {
    flex: 1,
    fontSize: '0.75rem',
    color: '#b91c1c',
    lineHeight: 1.4,
  },
  exportErrorDismiss: {
    flexShrink: 0,
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    color: '#b91c1c',
    fontSize: '1.125rem',
    lineHeight: 1,
    padding: '0 0.125rem',
  },
  exportProgressGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.25rem',
  },
  exportCancelIconButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '28px',
    height: '28px',
    padding: 0,
    background: 'transparent',
    border: `1px solid ${GRAY_300}`,
    borderRadius: '0.375rem',
    color: GRAY_500,
    fontSize: '1.125rem',
    lineHeight: 1,
    cursor: 'pointer',
    flexShrink: 0,
  },
  gridContainer: {
    flex: 1,
    width: '100%',
    minHeight: 0,
  },
};
